import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { ViewsEnumDeprecate } from '../../enums/viewsEnumDeprecate';
import HGeneric from '../../helpers/HGeneric';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { toggleSidebar, updateActiveView, updateSidebarDim } from '../../store/appSlice';
import CLocalizedText from '../CLocalizedText';
import SidebarButton from './SidebarButton';
import SidebarContents from './SidebarContents';

export default function Sidebar() {
  const sidebarExpandedWidth = 14.5;
  const sidebarCollapsedWidth = 4;

  const sidebarGradientHeight = 5;
  const sidebarBottomPadding = 5;

  const sidebarRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { headerDimensions, dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const sidebar = useAppSelector((state) => state.dashboard.sidebar);

  const minimizeSidebarIcon = sidebar.isExpanded ? <ChevronDoubleLeftIcon /> : <ChevronDoubleRightIcon />;

  const sidebarWidth = sidebar.isExpanded ? `${sidebarExpandedWidth}rem` : `${sidebarCollapsedWidth}rem`;

  const updateSidebarWidth = (isExpanded: boolean) => {
    const multiplier = isExpanded ? sidebarExpandedWidth : sidebarCollapsedWidth;

    dispatch(
      updateSidebarDim({
        w: document.documentElement.clientWidth >= 768 ? multiplier * HGeneric.getRemInPx() : 0
      })
    );
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
    updateSidebarWidth(!sidebar.isExpanded);
  };

  useEffect(() => {
    const enumString = location.pathname.split('/')[1] as unknown as ViewsEnumDeprecate;
    dispatch(updateActiveView(ViewsEnumDeprecate[enumString]));
  }, [location.pathname, dispatch]);

  useEffect(() => {
    updateSidebarWidth(sidebar.isExpanded);
  }, [headerDimensions.w]);

  return (
    <div
      ref={sidebarRef}
      className={'bg-teal-2 whitespace-nowrap select-none cursor-default fixed hidden md:block text-white'}
      style={{
        minWidth: sidebarWidth,
        width: sidebarWidth,
        height: dashboardOutletDimensions.h,
        top: headerDimensions.h,
        zIndex: 1
      }}
    >
      <div
        className={
          'flex flex-col p-2 py-3 text-sm overflow-y-auto overscroll-y-contain overflow-x-visible hide-scrollbar'
        }
        style={{
          height: `calc(${dashboardOutletDimensions.h}px - 4rem)`,
          paddingBottom: `${sidebarBottomPadding}rem`
        }}
      >
        <div className={'flex flex-col gap-4 md:gap-2'}>
          <SidebarContents />
        </div>
      </div>

      <div
        className={'pointer-events-none fixed left-0 bg-gradient-to-b from-teal-2/0 to-teal-2'}
        style={{
          bottom: '4rem',
          width: sidebarWidth,
          height: `${sidebarGradientHeight}rem`,
          zIndex: 20
        }}
      />

      <div
        className={'fixed left-0 bottom-0 bg-teal-2'}
        style={{
          width: sidebarWidth,
          zIndex: 30
        }}
      >
        <div className={'p-2 !pt-0'}>
          <SidebarButton
            label={<CLocalizedText dictKey={'globalSidebarMinimize'} />}
            onClick={handleToggleSidebar}
            isSelected={false}
            icon={minimizeSidebarIcon}
          />
        </div>
      </div>
    </div>
  );
}
